import _chunk from 'lodash/chunk';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';

import { DEFAULT_ACCORDION_EXCLUSIVE_PROP, DEFAULT_ACCORDION_STYLED_PROP } from './constants';
import {
  ACCORDION_BLOCK_ELEMENT_TYPE,
  ACCORDION_BLOCK_COMPONENT_NAME,
  ACCORDION_CONTENT_COMPONENT_NAME,
  ACCORDION_TITLE_COMPONENT_NAME,
  ACCORDION_ICON_COMPONENT_NAME,
} from '../../../../constants/types';
import { createTextBlockElement, createTextBlockState } from '../text-block';
import {
  createFishermanComponent,
  createSemanticComponent,
  getComponentId,
} from '../utils/components';
import {
  createComponentBaseProps,
  extractComponentBaseProps,
  getCollectionPropValue,
  getStaticPropValue,
} from '../utils/props';

export function createAccordionBlockElement(data = {}, context = {}) {
  const { id, className, style, panels = [], styled, exclusive, icon = 'dropdown' } = data;

  const defaultActiveIndexes = [];
  const childElements = panels.flatMap((panel = {}, index) => {
    const { id: panelId, title, content } = panel;
    if (_isEmpty(panel) || _isEmpty(title) || _isEmpty(content)) {
      return [];
    }

    if (panel.isActive) {
      defaultActiveIndexes.push(index);
    }

    return [
      createFishermanComponent(
        ACCORDION_TITLE_COMPONENT_NAME,
        `${panelId}-title`,
        [],
        [
          createSemanticComponent(
            ACCORDION_ICON_COMPONENT_NAME,
            null,
            [
              {
                name: 'name',
                resolve: icon,
              },
            ],
            [],
          ),
          createTextBlockElement(title, context),
        ],
      ),
      createFishermanComponent(
        ACCORDION_CONTENT_COMPONENT_NAME,
        `${panelId}-content`,
        [],
        [createTextBlockElement(content, context)],
      ),
    ];
  });

  return createFishermanComponent(
    ACCORDION_BLOCK_COMPONENT_NAME,
    id,
    [
      ...createComponentBaseProps(className, style),
      { name: 'styled', resolve: styled || false },
      { name: 'exclusive', resolve: exclusive || false },
      {
        name: 'defaultActiveIndexes',
        resolve: { type: 'COLLECTION', value: defaultActiveIndexes || [] },
      },
    ],
    childElements,
  );
}

export function getPanelPairs(accordionChildren) {
  if (_isEmpty(accordionChildren) || !_isArray(accordionChildren)) {
    return [];
  }

  return _chunk(accordionChildren, 2)
    .filter(
      ([title, content]) =>
        _get(title, 'name') === ACCORDION_TITLE_COMPONENT_NAME &&
        _get(content, 'name') === ACCORDION_CONTENT_COMPONENT_NAME,
    )
    .map(([title, content]) => ({ title, content }));
}

function createPanelsState(accordionChildren, context, defaultActiveIndexes) {
  const panelPairs = getPanelPairs(accordionChildren);

  return panelPairs.map(({ title, content }, index) => {
    const panelTitle = createTextBlockState(_get(title, 'children[1]'), context);
    const panelContent = createTextBlockState(_get(content, 'children[0]'), context);
    const icon = getStaticPropValue(_get(title, 'children[0].props'), 'name', 'dropdown');
    const isActive = defaultActiveIndexes.includes(index);
    const id = title.id.replace('-title', '') || content.id.replace('-content', '');

    return {
      id,
      title: panelTitle,
      content: panelContent,
      icon,
      isActive,
    };
  });
}

export function createAccordionBlockState(recipeSnippet = {}, context = {}) {
  const { props, children = [] } = recipeSnippet || {};

  const type = ACCORDION_BLOCK_ELEMENT_TYPE;
  const { className, style } = extractComponentBaseProps(recipeSnippet);
  const id = getComponentId(recipeSnippet);
  const exclusive = getStaticPropValue(props, 'exclusive', DEFAULT_ACCORDION_EXCLUSIVE_PROP);
  const styled = getStaticPropValue(props, 'styled', DEFAULT_ACCORDION_STYLED_PROP);
  const defaultActiveIndexes = getCollectionPropValue(props, 'defaultActiveIndexes') || [];
  const panelsState = createPanelsState(children, context, defaultActiveIndexes);

  return {
    id,
    style,
    className,
    type,
    panels: panelsState,
    exclusive,
    styled,
  };
}

export function createAccordionBlockTemplateState(data = {}) {
  const {
    panels,
    componentIdentifier,
    componentConfigurationId,
    content,
    contentName,
    contentNodeConfigurationId,
    ...restAccordion
  } = data;

  const panelsTemplateState = panels.map((panel) => ({
    id: panel.id,
    title: panel.title,
    content: panel.content,
  }));

  return {
    ...restAccordion,
    panels: panelsTemplateState,
  };
}
